import { useMemo, useState } from 'react';
import { defaultCustomDates, getRangeBounds, type RangeKey } from '../lib/range';

const KEY = 'sf_range';

type StoredRange = {
  key: RangeKey;
  from?: string;
  to?: string;
};

function isRangeKey(value: string): value is RangeKey {
  return value === 'today' || value === 'yesterday' || value === '7d' || value === '30d' || value === 'custom';
}

function readStored(): StoredRange {
  const fallback = { key: 'today' as RangeKey, ...defaultCustomDates() };
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return fallback;
    if (isRangeKey(raw)) {
      return { key: raw, ...defaultCustomDates() };
    }
    const parsed = JSON.parse(raw) as StoredRange;
    if (!parsed || !isRangeKey(parsed.key)) return fallback;
    const custom = defaultCustomDates();
    return {
      key: parsed.key,
      from: parsed.from || custom.from,
      to: parsed.to || custom.to,
    };
  } catch {
    return fallback;
  }
}

function persist(next: StoredRange) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function useDateRange() {
  const initial = readStored();
  const [key, setKey] = useState<RangeKey>(initial.key);
  const [customFrom, setCustomFrom] = useState(initial.from || defaultCustomDates().from);
  const [customTo, setCustomTo] = useState(initial.to || defaultCustomDates().to);

  const setRange = (next: RangeKey) => {
    setKey(next);
    persist({ key: next, from: customFrom, to: customTo });
  };

  const setCustomRange = (from: string, to: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    setKey('custom');
    persist({ key: 'custom', from, to });
  };

  const bounds = useMemo(
    () => getRangeBounds(key, new Date(), { from: customFrom, to: customTo }),
    [key, customFrom, customTo]
  );

  return { key, setRange, customFrom, customTo, setCustomRange, bounds };
}
